// Chart.js custom plugins

// Helper to detect dark mode
const isDarkMode = () => document.documentElement.getAttribute('data-bs-theme') === 'dark';

// Plugin to shade the forecast area (right of "now" line) with a subtle background
export const forecastAreaPlugin = {
  id: "forecastArea",
  beforeDatasetsDraw(chart: any, _args: any, pluginOptions: any) {
    const nowIndex = pluginOptions?.nowIndex;
    if (nowIndex === undefined || nowIndex === null || nowIndex < 0) return;

    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data || !meta.data[nowIndex]) return;

    const x = meta.data[nowIndex].x;
    const { top, bottom, right } = chart.chartArea;
    const ctx = chart.ctx;

    ctx.save();
    ctx.fillStyle = isDarkMode()
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.045)';
    ctx.fillRect(x, top, right - x, bottom - top);
    ctx.restore();
  },
};

// Plugin to draw a vertical line at the current time (now)
export const nowLinePlugin = {
  id: "forecastNowLine",
  afterDatasetsDraw(chart: any, _args: any, pluginOptions: any) {
    const nowIndex = pluginOptions?.nowIndex;
    if (nowIndex === undefined || nowIndex === null || nowIndex < 0) return;

    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data || !meta.data[nowIndex]) return;

    const x = meta.data[nowIndex].x;
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.lineWidth = 2;
    ctx.strokeStyle = isDarkMode()
      ? 'rgba(255, 255, 255, 0.6)'
      : 'rgba(0, 0, 0, 0.8)';
    ctx.stroke();
    ctx.restore();
  },
};

// Plugin to show hover line and plus icon in prediction mode
export const predictionHoverPlugin = {
  id: "predictionHover",
  afterDatasetsDraw(chart: any, _args: any, pluginOptions: any) {
    const predictionMode = pluginOptions?.predictionMode;
    if (!predictionMode) return;

    const { chartArea, ctx, scales } = chart;
    const canvasPosition = chart._lastHoverPosition;

    if (!canvasPosition) return;

    const { x, y } = canvasPosition;

    // Only draw if within chart area
    if (x < chartArea.left || x > chartArea.right || y < chartArea.top || y > chartArea.bottom) return;

    ctx.save();

    // Draw vertical line
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(74, 144, 226, 0.5)';
    ctx.setLineDash([5, 5]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw horizontal line
    ctx.beginPath();
    ctx.moveTo(chartArea.left, y);
    ctx.lineTo(chartArea.right, y);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(74, 144, 226, 0.5)';
    ctx.setLineDash([5, 5]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Get values from scales
    const xScale = scales.x;
    const yScale = scales.y;

    if (xScale && yScale) {
      const xValue = xScale.getValueForPixel(x);
      const yValue = yScale.getValueForPixel(y);

      // Get the label (datetime) and format the price
      const labels = chart.data.labels || [];
      const labelIndex = Math.round(xValue);

      if (labelIndex >= 0 && labelIndex < labels.length && yValue !== undefined) {
        const datetime = labels[labelIndex];
        const price = yValue.toFixed(2);

        // Draw info overlay box
        const padding = 10;
        const lineHeight = 18;
        const text1 = `${datetime}`;
        const text2 = `Price: ${price}`;

        // Measure text width
        ctx.font = 'bold 12px sans-serif';
        const text1Width = ctx.measureText(text1).width;
        const text2Width = ctx.measureText(text2).width;
        const boxWidth = Math.max(text1Width, text2Width) + padding * 2;
        const boxHeight = lineHeight * 2 + padding * 2;

        // Position the box near the cursor, but keep it within bounds
        let boxX = x + 15;
        let boxY = y - boxHeight / 2;

        // Adjust if box goes off right edge
        if (boxX + boxWidth > chartArea.right) {
          boxX = x - boxWidth - 15;
        }

        // Adjust if box goes off top or bottom
        if (boxY < chartArea.top) {
          boxY = chartArea.top + 5;
        } else if (boxY + boxHeight > chartArea.bottom) {
          boxY = chartArea.bottom - boxHeight - 5;
        }

        // Draw box background
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.fillRect(boxX, boxY, boxWidth, boxHeight);

        // Draw box border
        ctx.strokeStyle = 'rgba(74, 144, 226, 1)';
        ctx.lineWidth = 2;
        ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

        // Draw text
        ctx.fillStyle = 'white';
        ctx.font = 'bold 12px sans-serif';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(text1, boxX + padding, boxY + padding + lineHeight / 2);
        ctx.fillText(text2, boxX + padding, boxY + padding + lineHeight * 1.5);
      }
    }

    // Draw plus icon
    const iconSize = 20;
    const iconX = x;
    const iconY = y;

    // Circle background
    ctx.beginPath();
    ctx.arc(iconX, iconY, iconSize / 2, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(74, 144, 226, 0.9)';
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Plus sign
    ctx.beginPath();
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'white';
    // Vertical line of plus
    ctx.moveTo(iconX, iconY - 6);
    ctx.lineTo(iconX, iconY + 6);
    // Horizontal line of plus
    ctx.moveTo(iconX - 6, iconY);
    ctx.lineTo(iconX + 6, iconY);
    ctx.stroke();

    ctx.restore();
  },
};

// Plugin to render user profile images as points
export const userImagePlugin = {
  id: "userImages",
  afterDatasetsDraw(chart: any) {
    const { ctx, data } = chart;

    data.datasets.forEach((dataset: any, datasetIndex: number) => {
      // Only process user prediction datasets
      if (!dataset.metadata?.isUserPrediction) return;

      const meta = chart.getDatasetMeta(datasetIndex);
      const imageUrl = dataset.metadata?.userImageUrl;

      meta.data.forEach((point: any, index: number) => {
        if (!point || dataset.data[index] === null) return;

        const { x, y } = point;
        const radius = dataset.pointRadius || 8;

        ctx.save();

        // If user has profile image, try to draw it
        if (imageUrl) {
          // Create image element (cached)
          if (!chart._userImageCache) {
            chart._userImageCache = {};
          }

          if (!chart._userImageCache[imageUrl]) {
            const img = new Image();
            img.src = imageUrl;
            img.onload = () => {
              chart.draw(); // Redraw when image loads
            };
            chart._userImageCache[imageUrl] = img;
          }

          const img = chart._userImageCache[imageUrl];

          if (img.complete && img.naturalHeight !== 0) {
            // Draw circular clipped image
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, 2 * Math.PI);
            ctx.closePath();
            ctx.clip();

            ctx.drawImage(
              img,
              x - radius,
              y - radius,
              radius * 2,
              radius * 2
            );

            ctx.restore();
            ctx.save();

            // Draw border around image
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, 2 * Math.PI);
            ctx.strokeStyle = dataset.pointBorderColor || 'rgba(255, 255, 255, 1)';
            ctx.lineWidth = dataset.pointBorderWidth || 2;
            ctx.stroke();
          } else {
            // Fallback to colored circle if image not loaded
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, 2 * Math.PI);
            ctx.fillStyle = dataset.pointBackgroundColor || 'rgba(255, 193, 7, 1)';
            ctx.fill();
            ctx.strokeStyle = dataset.pointBorderColor || 'rgba(255, 255, 255, 1)';
            ctx.lineWidth = dataset.pointBorderWidth || 2;
            ctx.stroke();
          }
        } else {
          // No image URL - draw user icon or colored circle
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, 2 * Math.PI);
          ctx.fillStyle = dataset.pointBackgroundColor || 'rgba(255, 193, 7, 1)';
          ctx.fill();
          ctx.strokeStyle = dataset.pointBorderColor || 'rgba(255, 255, 255, 1)';
          ctx.lineWidth = dataset.pointBorderWidth || 2;
          ctx.stroke();

          // Draw simple user icon (head + body)
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          // Head
          ctx.beginPath();
          ctx.arc(x, y - radius * 0.2, radius * 0.3, 0, 2 * Math.PI);
          ctx.fill();
          // Body
          ctx.beginPath();
          ctx.arc(x, y + radius * 0.4, radius * 0.4, 0, Math.PI, true);
          ctx.fill();
        }

        ctx.restore();
      });
    });
  },
};
